import type { CcmCommunicationId, CcmSectionId } from '@arhs-fleetback/types/entities/ccm';
import { createSelector } from 'reselect';

import { getProps, getSecondProps } from 'utils/selectors';

import type { CcmReducer } from '../reducers';
import { resolveEmailPlaceholders, resolveSegments, resolveSmsPlaceholders } from '../utils';

type State = { ccm: CcmReducer };

const getState = (state: State) => state.ccm;

const getConfig = createSelector(getState, ccm => ccm.config);
export const getCcmConfig = createSelector(getConfig, config => config.data);
export const getCcmConfigError = createSelector(getConfig, config => config.error);
export const isCcmConfigFetching = createSelector(getConfig, config => config.isFetching);
export const getCcmConfigSections = createSelector(getCcmConfig, ccmConfig => ccmConfig?.sections);
export const getCcmConfigMaxLengths = createSelector(getCcmConfig, ccmConfig => ccmConfig?.maxLengths);
export const getCcmConfigById = createSelector(getCcmConfig, getProps<CcmSectionId>, (config, sectionId) => config?.sections?.[sectionId]);

export const getCcmCommunicationConfigById = createSelector(
  getCcmConfigById,
getSecondProps<CcmCommunicationId>,
(section, communicationId) => section?.communications?.[communicationId as CcmCommunicationId] ?? {}
);

export const getCcmCommunicationConfigHasSms = createSelector(
  getCcmCommunicationConfigById,
  (communicationConfig) => communicationConfig.sms ?? false
);

export const getCcmCommunicationConfigHasEmail = createSelector(
  getCcmCommunicationConfigById,
  (communicationConfig) => communicationConfig.email ?? false
);

const getCcmCommunicationConfigPlaceholders = createSelector(
  getCcmCommunicationConfigById,
  (communicationConfig) => communicationConfig.placeholders ?? {}
);

export const getSectionConfigSegments = createSelector(
  getCcmConfigById,
  getCcmCommunicationConfigById,
  (sectionConfig, communicationConfig) => resolveSegments(sectionConfig, communicationConfig)
);

const getSectionConfigPlaceholders = createSelector(getCcmConfigById, (ccmConfig) => ccmConfig?.placeholders ?? {});
export const getSmsPlaceHolders = createSelector(
  getSectionConfigPlaceholders,
  getCcmCommunicationConfigPlaceholders,
  (sectionPlaceholders, communicationPlaceholders) =>
    resolveSmsPlaceholders(sectionPlaceholders, communicationPlaceholders)
);
export const getEmailPlaceHolders = createSelector(
  getSectionConfigPlaceholders,
  getCcmCommunicationConfigPlaceholders,
  (sectionPlaceholders, communicationPlaceholders) =>
    resolveEmailPlaceholders(sectionPlaceholders, communicationPlaceholders)
);

const getDataSlice = createSelector(getState, ccm => ccm.data);
export const getCcmData = createSelector(getDataSlice, data => data.content);
export const getCcmLanguage = createSelector(getDataSlice, data => data.language);
export const isCcmDataSaving = createSelector(getDataSlice, data => data.isSaving);
export const isCcmDataFetching = createSelector(getDataSlice, data => data.isFetching);

export const isCcmLoading = createSelector(getDataSlice, getConfig, (data, config) => data?.isFetching || config?.isFetching);

export const hasCcm = createSelector(getCcmConfig, getCcmData, (config, data) => config !== null && data !== null);
